import React, { useEffect, useMemo, useState } from "react";
import {
  addDays, addMonths, addWeeks, endOfMonth, endOfWeek, format, isSameDay, isSameMonth,
  parseISO, startOfMonth, startOfWeek,
} from "date-fns";
import { it } from "date-fns/locale";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Clock, User, UserCheck, AlignLeft, MapPin, CalendarClock, Flag } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const PALETTE = ["#008C45", "#0369A1", "#B45309", "#7C3AED", "#BE123C", "#0F766E", "#4338CA", "#A16207"];
const VIEWS = [["month", "Mese"], ["week", "Settimana"], ["day", "Giorno"], ["upcoming", "Prossimi 10"]];
const iso = (d) => format(d, "yyyy-MM-dd");
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const rangeFor = (view, cursor) => {
  if (view === "month") return [startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 }), endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 })];
  if (view === "week") return [startOfWeek(cursor, { weekStartsOn: 1 }), endOfWeek(cursor, { weekStartsOn: 1 })];
  return [cursor, cursor];
};

const titleFor = (view, cursor) => {
  if (view === "month") return cap(format(cursor, "MMMM yyyy", { locale: it }));
  if (view === "week") {
    const [s, e] = rangeFor("week", cursor);
    return `${format(s, "d MMM", { locale: it })} – ${format(e, "d MMM yyyy", { locale: it })}`;
  }
  if (view === "day") return cap(format(cursor, "EEEE d MMMM yyyy", { locale: it }));
  return "Prossimi 10 eventi in programma";
};

export function FinishFlag({ size = 12, className = "", testid }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" className={`inline shrink-0 ${className}`} data-testid={testid} aria-label="Ultima lezione">
      <rect x="1" y="1" width="1.6" height="14" fill="currentColor" />
      <rect x="3" y="1.5" width="12" height="8" fill="#fff" stroke="#111" strokeWidth="0.6" />
      {[0, 1, 2, 3].map((c) => [0, 1].map((r) => ((c + r) % 2 === 0) && <rect key={`${c}${r}`} x={3 + c * 3} y={1.5 + r * 4} width="3" height="4" fill="#111" />))}
    </svg>
  );
}

function EventChip({ e, color, compact, onOpen }) {
  const cancelled = e.status === "annullata";
  return (
    <button type="button" onClick={() => onOpen(e)} data-testid={`cal-event-${e.id}`} title={`${e.course_title} · Lez. ${e.number} · ${e.teacher || "docente n.d."}`}
      className={`block w-full text-left rounded px-1.5 py-0.5 text-[11px] leading-tight truncate text-white hover:opacity-85 transition-opacity ${cancelled ? "line-through opacity-50" : ""}`}
      style={{ backgroundColor: color }}>
      {e.is_first && <Flag size={10} className="inline -mt-0.5 mr-0.5" fill="currentColor" data-testid={`cal-first-${e.id}`} />}
      {e.is_last && <FinishFlag className="-mt-0.5 mr-0.5" testid={`cal-last-${e.id}`} />}
      {e.start_time || "--:--"} {e.course_code}{!compact && e.teacher ? ` · ${e.teacher}` : ""}
    </button>
  );
}

function EventRow({ e, color, showDate, onOpen }) {
  const cancelled = e.status === "annullata";
  return (
    <button type="button" onClick={() => onOpen(e)} data-testid={`cal-row-${e.id}`}
      className="w-full text-left flex items-stretch gap-3 rounded-lg border border-slate-200 bg-white hover:border-slate-400 transition-colors overflow-hidden">
      <span className="w-1.5 shrink-0" style={{ backgroundColor: color }} />
      {showDate && (
        <div className="py-2 w-16 shrink-0 text-center">
          <div className="text-[10px] uppercase text-slate-500">{format(parseISO(e.date), "EEE", { locale: it })}</div>
          <div className="text-xl font-bold tabular text-slate-900 leading-none">{format(parseISO(e.date), "d")}</div>
          <div className="text-[10px] uppercase text-slate-500">{format(parseISO(e.date), "MMM", { locale: it })}</div>
        </div>
      )}
      <div className={`py-2 pr-3 min-w-0 flex-1 ${cancelled ? "line-through opacity-60" : ""}`}>
        <div className="text-sm font-semibold text-slate-900 truncate flex items-center gap-1.5">
          {e.is_first && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-amber-700 bg-amber-100 rounded px-1.5 py-0.5 shrink-0" data-testid={`cal-row-first-${e.id}`}><Flag size={10} fill="currentColor" /> Avvio corso</span>}
          {e.is_last && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-slate-800 bg-slate-200 rounded px-1.5 py-0.5 shrink-0" data-testid={`cal-row-last-${e.id}`}><FinishFlag /> Ultima lezione</span>}
          <span className="truncate">{e.course_title}</span>
        </div>
        <div className="text-xs text-slate-500 truncate">{e.course_code} · Lezione {e.number}{e.title && e.title !== `Lezione ${e.number}` ? ` – ${e.title}` : ""}</div>
        <div className="flex flex-wrap gap-x-4 mt-1 text-xs text-slate-700">
          <span className="flex items-center gap-1"><Clock size={12} /> {e.start_time && e.end_time ? `${e.start_time} – ${e.end_time}` : "orario n.d."}</span>
          <span className="flex items-center gap-1"><User size={12} /> {e.teacher || "docente n.d."}</span>
        </div>
      </div>
    </button>
  );
}

function InfoLine({ icon: Icon, main, sub, testid }) {
  return (
    <div className="flex items-start gap-4" data-testid={testid}>
      <Icon size={18} className="text-slate-400 mt-0.5 shrink-0" />
      <div>
        <div className="text-slate-800">{main}</div>
        {sub && <div className="text-xs text-slate-400">{sub}</div>}
      </div>
    </div>
  );
}

function EventPreview({ e, color, onClose }) {
  return (
    <Dialog open={!!e} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="p-0 overflow-hidden max-w-md gap-0" data-testid="cal-preview">
        {e && (
          <>
            <div className="flex items-center gap-3 px-5 py-4 text-white" style={{ backgroundColor: color }}>
              <CalendarClock size={24} />
              <DialogTitle className="text-lg font-medium text-white" data-testid="cal-preview-date">{cap(format(parseISO(e.date), "EEEE d MMMM yyyy", { locale: it }))}</DialogTitle>
            </div>
            <div className="px-5 py-5">
              <div className="text-lg font-semibold text-slate-800 uppercase leading-snug" data-testid="cal-preview-title">{e.course_title}</div>
              <div className="text-xs text-slate-400 uppercase mt-1 mb-5">{[e.course_code, e.course_edition].filter(Boolean).join(" · ")}</div>
              <div className="space-y-4">
                {e.is_first && <div className="flex items-center gap-2 text-sm font-semibold text-amber-700" data-testid="cal-preview-first"><Flag size={16} fill="currentColor" /> Prima lezione: corso in partenza</div>}
                {e.is_last && <div className="flex items-center gap-2 text-sm font-semibold text-slate-800" data-testid="cal-preview-last"><FinishFlag size={16} /> Ultima lezione del corso: traguardo</div>}
                <InfoLine icon={AlignLeft} main={`Lezione ${e.number}${e.title && e.title !== `Lezione ${e.number}` ? ` – ${e.title}` : ""}`} testid="cal-preview-lesson" />
                <InfoLine icon={Clock} main={e.start_time && e.end_time ? `${e.start_time} – ${e.end_time}` : "Orario non indicato"} testid="cal-preview-time" />
                {e.course_platform && (
                  <InfoLine icon={MapPin} main={<span className="text-xs uppercase px-2.5 py-1 rounded-full bg-purple-50 text-purple-700">{e.course_platform}</span>} />
                )}
                <InfoLine icon={User} main={e.teacher || "Non indicato"} sub="Relatore" testid="cal-preview-teacher" />
                <InfoLine icon={UserCheck} main={e.tutor || "Non indicato"} sub="Tutor" testid="cal-preview-tutor" />
                {e.status === "annullata" && <div className="text-xs font-semibold text-rose-600 uppercase">Lezione annullata</div>}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MonthGrid({ cursor, events, colorOf, onOpen }) {
  const [s, e] = rangeFor("month", cursor);
  const days = [];
  for (let d = s; d <= e; d = addDays(d, 1)) days.push(d);
  const today = new Date();
  const [openDays, setOpenDays] = useState([]);
  const toggleDay = (k) => setOpenDays((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  return (
    <div data-testid="cal-month">
      <div className="grid grid-cols-7 text-[11px] font-semibold text-slate-500 uppercase mb-1">
        {["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"].map((d) => <div key={d} className="px-2 py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 border-t border-l border-slate-200">
        {days.map((d) => {
          const evs = events.filter((x) => x.date === iso(d));
          const expanded = openDays.includes(iso(d));
          return (
            <div key={iso(d)} className={`min-h-[92px] border-r border-b border-slate-200 p-1 ${isSameMonth(d, cursor) ? "bg-white" : "bg-slate-50"}`}>
              <div className={`text-xs mb-1 w-6 h-6 grid place-items-center rounded-full ${isSameDay(d, today) ? "bg-[#0B192C] text-white font-bold" : isSameMonth(d, cursor) ? "text-slate-700" : "text-slate-400"}`}>{format(d, "d")}</div>
              <div className="space-y-0.5">
                {(expanded ? evs : evs.slice(0, 3)).map((x) => <EventChip key={x.id} e={x} color={colorOf(x)} onOpen={onOpen} />)}
                {evs.length > 3 && (
                  <button type="button" onClick={() => toggleDay(iso(d))} className="text-[10px] font-medium text-[#1E3E62] hover:underline px-1" data-testid={`cal-more-${iso(d)}`}>
                    {expanded ? "Mostra meno" : `+${evs.length - 3} ${evs.length - 3 === 1 ? "altra" : "altre"}`}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WeekGrid({ cursor, events, colorOf, onOpen }) {
  const [s] = rangeFor("week", cursor);
  const today = new Date();
  return (
    <div className="grid grid-cols-1 md:grid-cols-7 gap-2" data-testid="cal-week">
      {[0, 1, 2, 3, 4, 5, 6].map((i) => {
        const d = addDays(s, i);
        const evs = events.filter((x) => x.date === iso(d));
        return (
          <div key={i} className={`rounded-lg border p-2 min-h-[140px] ${isSameDay(d, today) ? "border-[#0B192C]" : "border-slate-200"}`}>
            <div className="text-xs font-semibold text-slate-600 mb-2">{cap(format(d, "EEE d MMM", { locale: it }))}</div>
            <div className="space-y-1.5">
              {evs.map((x) => (
                <button type="button" key={x.id} onClick={() => onOpen(x)} data-testid={`cal-event-${x.id}`}
                  className={`block w-full text-left rounded-md p-1.5 text-white text-[11px] leading-snug hover:opacity-85 transition-opacity ${x.status === "annullata" ? "line-through opacity-50" : ""}`}
                  style={{ backgroundColor: colorOf(x) }}>
                  <div className="font-semibold">{x.is_first && <Flag size={10} className="inline -mt-0.5 mr-0.5" fill="currentColor" data-testid={`cal-first-${x.id}`} />}{x.is_last && <FinishFlag className="-mt-0.5 mr-0.5" testid={`cal-last-${x.id}`} />}{x.start_time}–{x.end_time}</div>
                  <div>{x.course_code} · Lez. {x.number}</div>
                  <div className="opacity-90 truncate">{x.teacher || "docente n.d."}</div>
                </button>
              ))}
              {evs.length === 0 && <div className="text-[11px] text-slate-300">—</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function LessonCalendar() {
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    const req = view === "upcoming"
      ? api.get("/calendar", { params: { upcoming: 10 } })
      : api.get("/calendar", { params: { start: iso(rangeFor(view, cursor)[0]), end: iso(rangeFor(view, cursor)[1]) } });
    req.then((r) => setEvents(r.data));
  }, [view, cursor]);

  const colorOf = (e) => PALETTE[(e.color_idx || 0) % PALETTE.length];

  const move = (dir) => setCursor((c) => (view === "month" ? addMonths(c, dir) : view === "week" ? addWeeks(c, dir) : addDays(c, dir)));
  const legend = useMemo(() => [...new Map(events.map((e) => [e.course_id, e])).values()], [events]);

  return (
    <Card className="p-4 border-slate-200" data-testid="lesson-calendar">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {view !== "upcoming" && (
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => move(-1)} data-testid="cal-prev"><ChevronLeft size={16} /></Button>
            <Button variant="outline" size="sm" className="h-8" onClick={() => setCursor(new Date())} data-testid="cal-today">Oggi</Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => move(1)} data-testid="cal-next"><ChevronRight size={16} /></Button>
          </div>
        )}
        <h3 className="font-display font-bold text-slate-900" data-testid="cal-title">{titleFor(view, cursor)}</h3>
        <div className="ml-auto inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50">
          {VIEWS.map(([k, l]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`cal-view-${k}`}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${view === k ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>{l}</button>
          ))}
        </div>
      </div>

      {view === "month" && <MonthGrid cursor={cursor} events={events} colorOf={colorOf} onOpen={setPreview} />}
      {view === "week" && <WeekGrid cursor={cursor} events={events} colorOf={colorOf} onOpen={setPreview} />}
      {(view === "day" || view === "upcoming") && (
        <div className="space-y-2" data-testid={`cal-${view}`}>
          {events.map((e) => <EventRow key={e.id} e={e} color={colorOf(e)} showDate={view === "upcoming"} onOpen={setPreview} />)}
          {events.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">{view === "day" ? "Nessuna lezione in questa giornata." : "Nessun evento in programma."}</div>}
        </div>
      )}

      {legend.length > 0 && view !== "upcoming" && view !== "day" && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-slate-600" data-testid="cal-legend">
          {legend.map((e) => <span key={e.course_id} className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: colorOf(e) }} />{e.course_code}</span>)}
        </div>
      )}
      <EventPreview e={preview} color={preview ? colorOf(preview) : undefined} onClose={() => setPreview(null)} />
    </Card>
  );
}
